/**
 * Isolated eval wrappers for template expressions.
 * Template JSON uses expressions like: "this.doc.owner[0].firstName != undefined"
 * These functions provide the correct scope (doc reference) for eval.
 *
 * WARNING: These use eval() intentionally — template expressions are authored
 * by the form designer, not end users. Do not "fix" by removing eval.
 */

import DataSetManager from "../../../utils/dataSetManager.js";

/**
 * Evaluates a visibility expression. Returns true/false.
 * @param {string} expr - Expression string from template (e.g. "this.doc.owner[0].age > 18")
 * @param {{ current: object }} docRef - React ref to the form data object
 * @returns {boolean}
 */
export function evaluateVisibility(expr, docRef) {
  if (!expr && expr !== false) return true;
  try {
    const doc = docRef.current;
    // Template expressions use "this.doc.xxx" — we provide `this` context via closure
    const thisObj = { doc };
    const fn = function () {
      return eval(expr);
    };
    const ans = fn.call(thisObj);
    return ans !== false;
  } catch {
    return false;
  }
}

/**
 * Executes a function expression from the template. Returns the result.
 * Used for onChangeValue, RunBeforeSend, etc.
 * @param {string} expr - Expression string
 * @param {{ current: object }} docRef - React ref to the form data object
 * @param {object} [settings] - Form settings object (agentId, roles, etc.)
 * @param {object} [e] - Event argument from DevExtreme callback
 * @returns {*} Result of the expression, or false on error
 */
export function executeExpression(expr, docRef, settings, e, extraContext = {}) {
  if (!expr) return false;
  try {
    const doc = docRef.current;
    const i = e; // alias so expressions can use `i` like in test-form
    const thisObj = { doc, settings, DataSetManager, ...extraContext };
    const fn = function () {
      return eval(expr);
    };
    return fn.call(thisObj);
  } catch (err) {
    console.error('executeExpression error:', err, 'expr:', expr);
    return false;
  }
}
